// @ts-nocheck
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Mail, BarChart2, Rocket, MessageCircle, Calendar, TrendingUp,
  Users, ArrowRight, FileText, CheckCircle2, ChevronRight, Sparkles,
  Zap, ShieldCheck, Globe, MailOpen, MousePointerClick, Send,
  LayoutGrid, BookOpen, PlayCircle, Star,
} from "lucide-react";
import { PageContainer } from "../ui/page-container";
import PageHeader from "../ui/PageHeader";

const SectionCard = ({ title, subtitle, icon: HeaderIcon, iconBg, iconColor, accentColor, action, children, className = "", noPad = false }) => (
  <div className={`bg-surface dark:bg-dark-800 rounded-xl
    border border-line dark:border-dark-700
    shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden ${className}`}>
    {/* Accent top bar — identical to ModernKPICards */}
    {accentColor && <div className={`h-[2px] w-full ${accentColor}`} />}
    {(title || action) && (
      <div className="flex items-center justify-between px-4 pt-3 pb-2">
        <div className="flex items-center gap-2">
          {HeaderIcon && (
            <div className={`w-6 h-6 rounded-lg ${iconBg} flex items-center justify-center border border-line-subtle dark:border-dark-600`}>
              <HeaderIcon className={`w-3 h-3 ${iconColor}`} strokeWidth={2.2} />
            </div>
          )}
          <div>
            {title && <h3 className="text-[13px] font-bold text-content dark:text-gray-100 tracking-tight">{title}</h3>}
            {subtitle && <p className="text-[11px] font-medium text-content-muted dark:text-gray-400 mt-0.5">{subtitle}</p>}
          </div>
        </div>
        {action && <div className="flex-shrink-0">{action}</div>}
      </div>
    )}
    <div className={noPad ? "" : "px-4 pb-4"}>{children}</div>
  </div>
);

const HelpKPICard = ({ icon: Icon, value, label, sublabel, accentColor, accentHex, iconBg, iconColor }) => (
  <div className="group relative overflow-hidden bg-surface dark:bg-dark-800 rounded-xl
    border border-line dark:border-dark-700
    shadow-sm hover:shadow-md hover:-translate-y-0.5
    transition-all duration-200 cursor-default">
    <div className={`absolute top-0 inset-x-0 h-[2px] ${accentColor}`} />
    <div className="px-4 pt-3.5 pb-3">
      {/* icon + label */}
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-6 h-6 flex-shrink-0 ${iconBg} rounded-md flex items-center justify-center`}>
          <Icon className={`w-3 h-3 ${iconColor}`} strokeWidth={2} />
        </div>
        <span className="text-meta font-semibold text-content-secondary dark:text-gray-300 truncate">{label}</span>
      </div>
      {/* value */}
      <p className="text-[22px] font-bold text-content dark:text-white leading-none tracking-tight mb-2.5">
        {value}
      </p>
      {/* footer */}
      <div className="flex items-center gap-1.5 pt-2 border-t border-line-subtle dark:border-dark-700/60">
        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: accentHex }} />
        <span className="text-[11.5px] font-medium text-content-muted dark:text-gray-400 truncate">{sublabel}</span>
      </div>
    </div>
  </div>
);

const StepCard = ({ step, icon: Icon, title, description, accentColor, accentHex, iconBg, iconColor, isLast }) => (
  <div className="relative flex flex-col items-center text-center group">
    {!isLast && (
      <div className="hidden md:block absolute top-7 left-[calc(50%+2rem)] right-[-50%] h-px
        bg-gradient-to-r from-gray-200 dark:from-dark-600 to-transparent z-0" />
    )}
    <div className="relative z-10 mb-3">
      <div className={`w-14 h-14 rounded-xl ${iconBg} flex items-center justify-center mx-auto
        border border-line-subtle dark:border-dark-600 shadow-sm relative overflow-hidden`}>
        <div className={`absolute top-0 inset-x-0 h-[2px] ${accentColor}`} />
        <Icon className={`w-6 h-6 ${iconColor}`} strokeWidth={1.8} />
      </div>
      <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center
        text-white text-[10px] font-black shadow-sm"
        style={{ background: `linear-gradient(135deg, ${accentHex}cc, ${accentHex})` }}>
        {step}
      </span>
    </div>
    <h4 className="text-[13px] font-bold text-content dark:text-white mb-1 tracking-tight">{title}</h4>
    <p className="text-[11.5px] text-content-muted dark:text-gray-400 leading-[1.6] max-w-[160px] font-medium">{description}</p>
  </div>
);

const BenefitRow = ({ icon: Icon, title, description, accentColor, accentHex, iconBg, iconColor, isLast }) => (
  <div className={`flex items-center gap-3 py-2.5 ${!isLast ? "border-b border-line-subtle dark:border-dark-700/60" : ""}`}>
    <div className={`relative flex-shrink-0 w-7 h-7 ${iconBg} rounded-lg flex items-center justify-center overflow-hidden`}>
      <div className={`absolute top-0 inset-x-0 h-[2px] ${accentColor}`} />
      <Icon className={`w-3.5 h-3.5 ${iconColor}`} strokeWidth={2} />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[12.5px] font-bold text-content dark:text-gray-100 tracking-tight">{title}</p>
      <p className="text-[11.5px] font-medium text-content-muted dark:text-gray-400 leading-[1.55] mt-0.5">{description}</p>
    </div>
    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: accentHex }} />
  </div>
);

const QuickLinkCard = ({ icon: Icon, label, description, accentColor, iconBg, iconColor, onClick }) => (
  <button
    onClick={onClick}
    className="group w-full text-left relative overflow-hidden bg-surface dark:bg-dark-800 rounded-xl
      border border-line dark:border-dark-700
      shadow-sm hover:shadow-md hover:-translate-y-0.5
      transition-all duration-200 p-3 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
  >
    <div className={`absolute top-0 inset-x-0 h-[2px] ${accentColor}`} />
    <div className="flex items-center gap-2.5">
      <div className={`w-7 h-7 flex-shrink-0 ${iconBg} rounded-lg flex items-center justify-center
        border border-line-subtle dark:border-dark-600`}>
        <Icon className={`w-3.5 h-3.5 ${iconColor}`} strokeWidth={2} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[12.5px] font-bold text-content dark:text-gray-100 tracking-tight">{label}</p>
        <p className="text-micro font-medium text-content-muted dark:text-gray-400 mt-0.5 truncate">{description}</p>
      </div>
      <ArrowRight className="w-3.5 h-3.5 text-content-subtle dark:text-gray-500 flex-shrink-0
        group-hover:text-indigo-500 dark:group-hover:text-indigo-400
        group-hover:translate-x-0.5 transition-all duration-200" />
    </div>
  </button>
);

const Tag = ({ icon: Icon, label, color }) => (
  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold ${color} border`}>
    <Icon className="w-3 h-3 flex-shrink-0" />
    {label}
  </span>
);

export default function HelpView() {
  const navigate = useNavigate();

  const kpiStats = [
    {
      icon: LayoutGrid,
      value: "500+",
      label: "Templates",
      sublabel: "Ready to use",
      accentColor: "bg-gradient-to-r from-indigo-500 to-indigo-500",
      accentHex: "#6366f1",
      iconBg: "bg-indigo-50 dark:bg-indigo-900/20",
      iconColor: "text-indigo-600 dark:text-indigo-400",
    },
    {
      icon: Send,
      value: "99.9%",
      label: "Deliverability",
      sublabel: "Industry leading",
      accentColor: "bg-gradient-to-r from-emerald-400 to-teal-500",
      accentHex: "#10b981",
      iconBg: "bg-emerald-50 dark:bg-emerald-900/20",
      iconColor: "text-emerald-600 dark:text-emerald-400",
    },
    {
      icon: MailOpen,
      value: "38%",
      label: "Avg. Open Rate",
      sublabel: "Above industry avg.",
      accentColor: "bg-gradient-to-r from-violet-500 to-purple-500",
      accentHex: "#8b5cf6",
      iconBg: "bg-violet-50 dark:bg-violet-900/20",
      iconColor: "text-violet-600 dark:text-violet-400",
    },
    {
      icon: MousePointerClick,
      value: "5 min",
      label: "Setup Time",
      sublabel: "From signup to send",
      accentColor: "bg-gradient-to-r from-amber-400 to-orange-500",
      accentHex: "#f59e0b",
      iconBg: "bg-amber-50 dark:bg-amber-900/20",
      iconColor: "text-amber-600 dark:text-amber-400",
    },
  ];

  /* ── Steps ── */
  const steps = [
    {
      step: "1",
      icon: Mail,
      title: "Select Template",
      description: "Choose from hundreds of ready-to-use templates or build a custom one for your audience.",
      accentColor: "bg-gradient-to-r from-indigo-400 to-indigo-500",
      accentHex: "#6366f1",
      iconBg: "bg-indigo-50 dark:bg-indigo-900/20",
      iconColor: "text-indigo-600 dark:text-indigo-400",
    },
    {
      step: "2",
      icon: BarChart2,
      title: "Create Campaign",
      description: "Promote products and services to your customers to increase engagement and conversions.",
      accentColor: "bg-gradient-to-r from-violet-400 to-purple-500",
      accentHex: "#8b5cf6",
      iconBg: "bg-violet-50 dark:bg-violet-900/20",
      iconColor: "text-violet-600 dark:text-violet-400",
    },
    {
      step: "3",
      icon: Rocket,
      title: "Launch & Track",
      description: "Hit send and instantly monitor opens, clicks, and conversions with real-time analytics.",
      accentColor: "bg-gradient-to-r from-emerald-400 to-teal-500",
      accentHex: "#10b981",
      iconBg: "bg-emerald-50 dark:bg-emerald-900/20",
      iconColor: "text-emerald-600 dark:text-emerald-400",
    },
  ];

  /* ── Benefits (2 columns × 2 rows) ── */
  const benefits = [
    {
      icon: MessageCircle,
      title: "Easy To Use",
      description: "Create beautiful, high-converting email campaigns with our drag-and-drop editor in minutes.",
      accentColor: "bg-gradient-to-r from-indigo-400 to-cyan-400",
      accentHex: "#38bdf8",
      iconBg: "bg-indigo-50 dark:bg-indigo-900/20",
      iconColor: "text-indigo-600 dark:text-indigo-400",
    },
    {
      icon: Calendar,
      title: "Schedule Campaigns",
      description: "Send personalized emails from promotions and sales events at exactly the right time.",
      accentColor: "bg-gradient-to-r from-amber-400 to-orange-400",
      accentHex: "#f59e0b",
      iconBg: "bg-amber-50 dark:bg-amber-900/20",
      iconColor: "text-amber-600 dark:text-amber-400",
    },
    {
      icon: TrendingUp,
      title: "Grow Your Revenue",
      description: "Improve your ROI with targeted campaigns and data-driven insights, all in a few easy steps.",
      accentColor: "bg-gradient-to-r from-emerald-400 to-teal-500",
      accentHex: "#10b981",
      iconBg: "bg-emerald-50 dark:bg-emerald-900/20",
      iconColor: "text-emerald-600 dark:text-emerald-400",
    },
    {
      icon: Users,
      title: "Market Like a Pro",
      description: "Smooth implementation of marketing campaigns with audience segmentation and A/B testing.",
      accentColor: "bg-gradient-to-r from-violet-400 to-purple-500",
      accentHex: "#8b5cf6",
      iconBg: "bg-violet-50 dark:bg-violet-900/20",
      iconColor: "text-violet-600 dark:text-violet-400",
    },
  ];

  /* ── Quick links ── */
  const quickLinks = [
    {
      icon: Rocket,
      label: "Create a Campaign",
      description: "Launch your first campaign in minutes",
      accentColor: "bg-gradient-to-r from-indigo-500 to-indigo-500",
      iconBg: "bg-indigo-50 dark:bg-indigo-900/20",
      iconColor: "text-indigo-600 dark:text-indigo-400",
      onClick: () => navigate("/campaigns"),
    },
    {
      icon: LayoutGrid,
      label: "Browse Templates",
      description: "Pick a design that fits your brand",
      accentColor: "bg-gradient-to-r from-violet-400 to-purple-500",
      iconBg: "bg-violet-50 dark:bg-violet-900/20",
      iconColor: "text-violet-600 dark:text-violet-400",
      onClick: () => navigate("/templates"),
    },
    {
      icon: Users,
      label: "Manage Leads",
      description: "Import and organise your audience",
      accentColor: "bg-gradient-to-r from-emerald-400 to-teal-500",
      iconBg: "bg-emerald-50 dark:bg-emerald-900/20",
      iconColor: "text-emerald-600 dark:text-emerald-400",
      onClick: () => navigate("/leads"),
    },
    {
      icon: BarChart2,
      label: "View Analytics",
      description: "Track opens, clicks and conversions",
      accentColor: "bg-gradient-to-r from-amber-400 to-orange-500",
      iconBg: "bg-amber-50 dark:bg-amber-900/20",
      iconColor: "text-amber-600 dark:text-amber-400",
      onClick: () => navigate("/"),
    },
    {
      icon: BookOpen,
      label: "Read Documentation",
      description: "Deep-dive into all platform features",
      accentColor: "bg-gradient-to-r from-teal-400 to-cyan-500",
      iconBg: "bg-teal-50 dark:bg-teal-900/20",
      iconColor: "text-teal-600 dark:text-teal-400",
      onClick: () => navigate("/document"),
    },
    {
      icon: Star,
      label: "Upgrade Plan",
      description: "Unlock higher send limits and more",
      accentColor: "bg-gradient-to-r from-pink-400 to-rose-500",
      iconBg: "bg-pink-50 dark:bg-pink-900/20",
      iconColor: "text-pink-600 dark:text-pink-400",
      onClick: () => navigate("/billing"),
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        icon={<Sparkles className="w-5 h-5" />}
        iconBg="bg-indigo-50 dark:bg-indigo-900/20"
        iconColor="text-indigo-600 dark:text-indigo-400"
        accentColor="bg-gradient-to-r from-indigo-500 to-indigo-500"
        title="Help & Getting Started"
        subtitle="Everything you need to launch successful email marketing campaigns"
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {kpiStats.map((kpi) => (
          <HelpKPICard key={kpi.label} {...kpi} />
        ))}
      </div>

      <div className="mb-4">
        <SectionCard
          title="How it Works"
          subtitle="Three simple steps to your first campaign"
          icon={Zap}
          iconBg="bg-indigo-50 dark:bg-indigo-900/20"
          iconColor="text-indigo-600 dark:text-indigo-400"
          accentColor="bg-gradient-to-r from-indigo-400 via-violet-500 to-emerald-400"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-4 py-1">
            {steps.map((step, i) => (
              <StepCard key={step.step} {...step} isLast={i === steps.length - 1} />
            ))}
          </div>
        </SectionCard>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">

        <SectionCard
          title="Key Benefits"
          subtitle="Why teams love our platform"
          icon={CheckCircle2}
          iconBg="bg-emerald-50 dark:bg-emerald-900/20"
          iconColor="text-emerald-600 dark:text-emerald-400"
          accentColor="bg-gradient-to-r from-emerald-400 to-teal-500"
        >
          <div className="-mx-4 px-4">
            {benefits.map((b, i) => (
              <BenefitRow key={b.title} {...b} isLast={i === benefits.length - 1} />
            ))}
          </div>
        </SectionCard>

        <SectionCard
          title="Quick Actions"
          subtitle="Jump straight to what you need"
          icon={PlayCircle}
          iconBg="bg-violet-50 dark:bg-violet-900/20"
          iconColor="text-violet-600 dark:text-violet-400"
          accentColor="bg-gradient-to-r from-violet-400 to-purple-500"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {quickLinks.map((link) => (
              <QuickLinkCard key={link.label} {...link} />
            ))}
          </div>
        </SectionCard>
      </div>

      <div className="relative bg-gradient-to-br from-indigo-600 via-indigo-700 to-indigo-700
        rounded-2xl overflow-hidden shadow-lg">

        <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "24px 24px" }} />
        <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/5 pointer-events-none" />
        <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-white/5 pointer-events-none" />

        <div className="relative px-6 py-7 text-center">
          <div className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur-sm flex items-center
            justify-center mx-auto mb-4 border border-white/20">
            <Rocket className="w-5 h-5 text-white" strokeWidth={1.8} />
          </div>

          <h3 className="text-[18px] font-extrabold text-white tracking-tight mb-2 leading-tight">
            Convert leads into customers
          </h3>
          <p className="text-[12.5px] text-indigo-100/90 max-w-[380px] mx-auto leading-[1.7] font-medium mb-5">
            Automate your marketing in minutes. Capture leads, retain customers,
            analyse behaviour and track campaign performance — all in one place.
          </p>

          <div className="flex flex-wrap justify-center gap-2 mb-5">
            <Tag icon={ShieldCheck} label="Secure & reliable" color="bg-white/10 text-white border-white/20" />
            <Tag icon={Globe} label="Multi-channel reach" color="bg-white/10 text-white border-white/20" />
            <Tag icon={BarChart2} label="Real-time analytics" color="bg-white/10 text-white border-white/20" />
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-2.5">
            <button
              onClick={() => navigate("/campaigns")}
              className="inline-flex items-center gap-2 px-5 py-2
                bg-surface text-indigo-700 dark:text-indigo-300 text-[13px] font-bold rounded-xl
                shadow-sm hover:bg-indigo-50 dark:hover:bg-indigo-900/30 active:bg-indigo-100 dark:active:bg-indigo-900/30
                transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/50"
            >
              Get Started
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => navigate("/document")}
              className="inline-flex items-center gap-2 px-5 py-2
                bg-white/10 border border-white/20 text-white text-[13px] font-semibold rounded-xl
                hover:bg-white/20 active:bg-white/30
                transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/40"
            >
              <FileText className="w-3.5 h-3.5" />
              Read Docs
            </button>
          </div>
        </div>

        <div className="flex items-center justify-center gap-5 px-6 py-2.5 bg-black/10 border-t border-white/10">
          {[
            { icon: CheckCircle2, label: "Free to start" },
            { icon: ChevronRight, label: "Setup in 5 minutes" },
            { icon: ShieldCheck, label: "No credit card needed" },
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-1.5">
              <Icon className="w-3 h-3 text-blue-200" strokeWidth={2} />
              <span className="text-[11px] text-indigo-100/80 font-medium">{label}</span>
            </div>
          ))}
        </div>
      </div>

    </PageContainer>
  );
}